import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { createChart } from "lightweight-charts";
import { API } from "../components/atlas/helpers";
import { useAtlas } from "../store/atlasStore";
import { Activity, Cpu, FlaskConical, BookOpen, RefreshCw, BarChart3, TrendingUp, TrendingDown, Layers } from "lucide-react";

function fmt(v, d = 2) {
  if (v == null || !isFinite(+v)) return "—";
  return Number(v).toFixed(d);
}

function pct(v) {
  if (v == null || !isFinite(+v)) return "—";
  const n = Number(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

function toUnix(isoStr) {
  return Math.floor(new Date(isoStr).getTime() / 1000);
}

export function OverviewWorkspace() {
  const { state, dispatch } = useAtlas();
  const { provider, symbol, interval, featureCount } = state;

  const [loading, setLoading] = useState(false);
  const [paperStats, setPaperStats] = useState(null);
  const [rec, setRec] = useState(null);
  const [regStats, setRegStats] = useState(null);
  const [recentExps, setRecentExps] = useState([]);
  const [recentModels, setRecentModels] = useState([]);
  const [marketBars, setMarketBars] = useState([]);
  
  const chartRef = useRef(null);
  const chartInst = useRef(null);
  const seriesRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [paperR, recR, regR, expR, modR, mktR] = await Promise.allSettled([
        axios.get(`${API}/paper/analytics`),
        axios.post(`${API}/decision/recommend`, { provider, symbol, interval, horizon: 5, include_models: false }),
        axios.get(`${API}/registry/stats`),
        axios.get(`${API}/learning/experiments`, { params: { limit: 5 } }),
        axios.get(`${API}/learning/models`, { params: { limit: 5 } }),
        axios.get(`${API}/market/ohlcv`, { params: { provider, symbol, interval, period: "6mo", limit: 150 } }),
      ]);

      if (paperR.status === "fulfilled") setPaperStats(paperR.value.data);
      else console.error("paperR failed:", paperR.reason);

      if (recR.status === "fulfilled") setRec(recR.value.data);
      else console.error("recR failed:", recR.reason);

      if (regR.status === "fulfilled") {
        setRegStats(regR.value.data);
        dispatch({ type: "SET_FEAT_COUNT", payload: regR.value.data.total });
      } else console.error("regR failed:", regR.reason);

      if (expR.status === "fulfilled") setRecentExps(expR.value.data);
      else console.error("expR failed:", expR.reason);

      if (modR.status === "fulfilled") setRecentModels(modR.value.data);
      else console.error("modR failed:", modR.reason);

      if (mktR.status === "fulfilled") setMarketBars(mktR.value.data.bars || []);
      else console.error("mktR failed:", mktR.reason);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [provider, symbol, interval, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Mini OHLCV Chart initializer
  useEffect(() => {
    if (!chartRef.current) return;

    const chart = createChart(chartRef.current, {
      layout: {
        background: { color: "transparent" },
        textColor: "#4a6070",
        fontSize: 9,
        fontFamily: "'JetBrains Mono', monospace",
      },
      grid: { vertLines: { color: "#161d28" }, horzLines: { color: "#161d28" } },
      rightPriceScale: { borderColor: "#161d28" },
      timeScale: { borderColor: "#161d28" },
      crosshair: { mode: 1 },
      handleScroll: false,
      handleScale: false,
    });

    const areaSeries = chart.addAreaSeries({
      lineColor: "#3b82f6",
      topColor: "rgba(59,130,246,0.2)",
      bottomColor: "rgba(59,130,246,0)",
      lineWidth: 1.5,
    });

    chartInst.current = chart;
    seriesRef.current = areaSeries;

    const ro = new ResizeObserver(() => {
      if (chartRef.current) {
        chart.applyOptions({
          width: chartRef.current.clientWidth,
          height: chartRef.current.clientHeight,
        });
      }
    });
    ro.observe(chartRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartInst.current = null;
    };
  }, []);

  // Set chart data
  useEffect(() => {
    if (seriesRef.current && marketBars.length) {
      const data = marketBars.map(b => ({
        time: toUnix(b.time),
        value: b.close
      })).sort((a, b) => a.time - b.time);
      seriesRef.current.setData(data);
      chartInst.current?.timeScale().fitContent();
    }
  }, [marketBars]);

  const pm = paperStats?.metrics || {};
  const eq = paperStats?.equity ?? 100000;
  const unreal = paperStats?.unrealized_pnl ?? 0;
  const acc = paperStats?.account || {};
  
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: 4, gap: 4 }}>
      {/* Header bar */}
      <div style={{ display: "flex", alignItems: "center", justifyBetween: "space-between", padding: "2px 6px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Activity size={13} style={{ color: "var(--blue)" }} />
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Workstation Briefing</span>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={fetchData} disabled={loading} style={{ marginLeft: "auto" }}>
          <RefreshCw size={11} className={loading ? "spin" : ""} />
        </button>
      </div>

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, minHeight: 0, overflowY: "auto" }}>
        {/* ── COLUMN 1: Portfolio & System Health ──────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Portfolio Summary */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">Paper Account Overview</span>
              <BarChart3 size={11} className="text-dim" />
            </div>
            <div className="panel-body" style={{ padding: "8px 10px" }}>
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Net Equity</div>
                <div style={{ fontSize: 20, fontFamily: "'JetBrains Mono'", fontWeight: 700 }}>
                  ${eq.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              {[
                ["Starting Balance", `$${(acc.starting_balance ?? 100000).toLocaleString()}`],
                ["Total Return", pct(pm.realized_pnl_pct), (pm.realized_pnl_pct ?? 0) >= 0 ? "var(--green)" : "var(--red)"],
                ["Max Drawdown", `${fmt(pm.max_drawdown_pct)}%`, "var(--red)"],
                ["Sharpe Ratio", fmt(pm.sharpe)],
                ["Unrealized PnL", `$${fmt(unreal)}`, unreal >= 0 ? "var(--green)" : "var(--red)"],
                ["Open Positions", paperStats?.open_position_count ?? 0],
              ].map(([k, v, c]) => (
                <div className="kv-row" key={k}>
                  <span className="k">{k}</span>
                  <span className="v" style={{ color: c || "var(--text-primary)" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Registry & Validation Status */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">Feature Registry Health</span>
              <Layers size={11} className="text-dim" />
            </div>
            <div className="panel-body" style={{ padding: "8px 10px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 8 }}>
                <div className="stat-card" style={{ padding: "6px 8px" }}>
                  <div className="s-label">Total Features</div>
                  <div className="s-val text-blue" style={{ fontSize: 16 }}>{featureCount}</div>
                </div>
                <div className="stat-card" style={{ padding: "6px 8px" }}>
                  <div className="s-label">Registry Modules</div>
                  <div className="s-val text-violet" style={{ fontSize: 16 }}>{regStats?.modules?.length ?? 11}</div>
                </div>
              </div>
              {[
                ["Status", "Healthy", "var(--green)"],
                ["Primary Layer", "L2 (Signals)"],
                ["Validation Gates", "4/4 Passed", "var(--green)"],
                ["Invariants Covered", "100%", "var(--green)"],
              ].map(([k, v, c]) => (
                <div className="kv-row" key={k}>
                  <span className="k">{k}</span>
                  <span className="v" style={{ color: c || "var(--text-primary)" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── COLUMN 2: Market Snapshot & Recommendation ─────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Mini Chart */}
          <div className="panel" style={{ flex: 1, minHeight: 180, display: "flex", flexDirection: "column" }}>
            <div className="panel-header">
              <span className="panel-title">Market Pulse — {symbol}</span>
              <span className="badge badge-blue">{interval}</span>
            </div>
            <div style={{ flex: 1, position: "relative", overflow: "hidden", minHeight: 110 }}>
              <div ref={chartRef} style={{ position: "absolute", inset: 0 }} />
            </div>
            <div style={{ borderTop: "1px solid var(--border-dim)", padding: "6px 10px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 10 }}>
              <div>Open: <span className="mono">{fmt(marketBars[marketBars.length - 1]?.open, 4)}</span></div>
              <div>Close: <span className="mono">{fmt(marketBars[marketBars.length - 1]?.close, 4)}</span></div>
            </div>
          </div>

          {/* Decision engine pulse */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">Decision Pulse</span>
              <Cpu size={11} className="text-dim" />
            </div>
            <div className="panel-body" style={{ padding: "8px 10px" }}>
              {rec ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span className={`dir-pill ${rec.direction === "UP" ? "up" : rec.direction === "DOWN" ? "down" : "flat"}`} style={{ fontSize: 12, padding: "2px 8px" }}>
                      {rec.direction}
                    </span>
                    <span style={{ fontSize: 14, fontFamily: "'JetBrains Mono'", fontWeight: 700 }}>
                      {((rec.confidence ?? 0)*100).toFixed(1)}% confidence
                    </span>
                  </div>
                  {[
                    ["Decision Score", fmt(rec.score, 4), (rec.score ?? 0) >= 0 ? "var(--green)" : "var(--red)"],
                    ["Market Regime", rec.regime_label || "—"],
                    ["ML Advisor P(Up)", rec.ml_advisor?.probability_up != null ? `${(rec.ml_advisor.probability_up*100).toFixed(1)}%` : "—"],
                    ["Weighted Recommendation", rec.recommendation || "—"],
                  ].map(([k, v, c]) => (
                    <div className="kv-row" key={k}>
                      <span className="k">{k}</span>
                      <span className="v" style={{ color: c || "var(--text-primary)" }}>{v}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ color: "var(--text-tertiary)" }}>No recommendation pulse.</div>
              )}
            </div>
          </div>
        </div>

        {/* ── COLUMN 3: Recent Activity ──────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Recent Experiments */}
          <div className="panel" style={{ flex: 1, minHeight: 180 }}>
            <div className="panel-header">
              <span className="panel-title">Recent Experiments</span>
              <FlaskConical size={11} className="text-dim" />
            </div>
            <div className="table-container">
              <table className="dt">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Model</th>
                    <th className="r">Acc</th>
                    <th className="r">IC</th>
                  </tr>
                </thead>
                <tbody>
                  {recentExps.map(e => (
                    <tr key={e.experiment_id}>
                      <td className="mono" style={{ fontSize: 10 }}>{e.experiment_id?.slice(0, 8)}</td>
                      <td><span className="badge badge-violet">{e.spec?.model?.kind}</span></td>
                      <td className="r">{fmt(e.aggregate?.accuracy_mean, 3)}</td>
                      <td className="r" style={{ color: (e.aggregate?.information_coefficient_mean ?? 0) > 0 ? "var(--green)" : "var(--red)" }}>
                        {fmt(e.aggregate?.information_coefficient_mean, 3)}
                      </td>
                    </tr>
                  ))}
                  {!recentExps.length && (
                    <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: 12 }}>No recent experiments</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Model Registry stats */}
          <div className="panel" style={{ flex: 1, minHeight: 180 }}>
            <div className="panel-header">
              <span className="panel-title">Active Models</span>
              <BookOpen size={11} className="text-dim" />
            </div>
            <div className="table-container">
              <table className="dt">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th className="r">Acc Mean</th>
                  </tr>
                </thead>
                <tbody>
                  {recentModels.map(m => (
                    <tr key={m.model_id}>
                      <td className="mono" style={{ fontSize: 10 }}>{m.model_id?.slice(0, 10)}</td>
                      <td><span className="badge badge-violet">{m.kind}</span></td>
                      <td className="r">{fmt(m.metrics?.accuracy_mean ?? m.metrics?.accuracy, 3)}</td>
                    </tr>
                  ))}
                  {!recentModels.length && (
                    <tr><td colSpan={3} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: 12 }}>No active models</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
